import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/utils/combine';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type CustomRole,
  type HierarchyChange,
  type HierarchyLayoutLevel,
  type MemberRole,
  type RoleHierarchy,
  roleLabelKey,
  useRoleHierarchy,
  useSaveRoleHierarchy,
} from '@/entities/collab';
import { FormError } from '@/shared/forms';
import { Button, Combobox, Dialog, ErrorState, Icons, LoadingState, useToast } from '@/shared/ui';

import styles from './Hierarchy.module.css';

const ROLE_DRAG = 'hierarchy-role';
const MAX_GAP = 9;

function toLayout(hierarchy: RoleHierarchy): HierarchyLayoutLevel[] {
  return hierarchy.levels
    .filter((level) => level.key !== 'owner')
    .map((level) => ({ key: level.key, custom_role_ids: [...level.custom_role_ids] }));
}

function sameLayout(a: HierarchyLayoutLevel[], b: HierarchyLayoutLevel[]): boolean {
  return (
    a.length === b.length &&
    a.every(
      (level, i) =>
        level.key === b[i]?.key &&
        level.custom_role_ids.length === b[i].custom_role_ids.length &&
        level.custom_role_ids.every((id, j) => id === b[i]?.custom_role_ids[j]),
    )
  );
}

/**
 * The shared role hierarchy: horizontal levels, several roles per level. Seniority only decides
 * who may manage whom - it never grants permissions. Custom roles are dragged between levels
 * (or moved with the arrow buttons); the draft is reviewed (dry run) before it is applied.
 */
export function HierarchyEditor({
  workspaceId,
  roles,
  canEdit,
}: {
  workspaceId: string;
  roles: readonly CustomRole[];
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const query = useRoleHierarchy(workspaceId);
  const save = useSaveRoleHierarchy(workspaceId);
  const [draft, setDraft] = useState<HierarchyLayoutLevel[] | null>(null);
  const [preview, setPreview] = useState<{
    changes: HierarchyChange[];
    added: number;
    removed: number;
  } | null>(null);
  const [counter, setCounter] = useState(1);
  const byId = useMemo(() => new Map(roles.map((role) => [role.id, role])), [roles]);

  if (query.isPending) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} />;
  const hierarchy = query.data;
  const server = toLayout(hierarchy);
  const layout = draft ?? server;
  const dirty = draft !== null && !sameLayout(draft, server);
  const editable = canEdit && hierarchy.can_edit;
  const meta = new Map(hierarchy.levels.map((level) => [level.key, level]));

  const update = (next: HierarchyLayoutLevel[]) => {
    setDraft(next);
  };
  const moveRole = (roleId: string, toKey: string) => {
    update(
      layout.map((level) => {
        const without = level.custom_role_ids.filter((id) => id !== roleId);
        return level.key === toKey
          ? { ...level, custom_role_ids: [...without, roleId] }
          : { ...level, custom_role_ids: without };
      }),
    );
  };
  const shiftRole = (roleId: string, delta: -1 | 1) => {
    const from = layout.findIndex((level) => level.custom_role_ids.includes(roleId));
    const to = layout[from + delta];
    if (to) moveRole(roleId, to.key);
  };
  const insertAfter = (index: number) => {
    const key = `new:${String(counter)}`;
    setCounter(counter + 1);
    const next = [...layout];
    next.splice(index + 1, 0, { key, custom_role_ids: [] });
    update(next);
  };
  const removeLevel = (key: string) => {
    update(layout.filter((level) => level.key !== key));
  };
  const shiftLevel = (index: number, delta: -1 | 1) => {
    const next = [...layout];
    const other = next[index + delta];
    const current = next[index];
    if (!other || !current) return;
    next[index + delta] = current;
    next[index] = other;
    update(next);
  };
  /** Custom levels in the gap that ``index`` starts: the gap is limited to 9 levels. */
  const gapIsFull = (index: number) => {
    let upper = index;
    while (upper > 0 && !layout[upper]?.key.startsWith('std:')) upper -= 1;
    let lower = index + 1;
    while (lower < layout.length - 1 && !layout[lower]?.key.startsWith('std:')) lower += 1;
    return lower - upper - 1 >= MAX_GAP;
  };
  const payload = (dryRun: boolean, confirm = false) => ({
    expected_revision: hierarchy.revision,
    levels: layout,
    dry_run: dryRun,
    confirm_level_removal: confirm,
  });

  return (
    <section className="hierarchy-editor" aria-label={t('workspaceAdmin.hierarchy.title')}>
      <p>{t('workspaceAdmin.hierarchy.intro')}</p>
      <div className={styles.hierarchy}>
        {hierarchy.levels
          .filter((level) => level.key === 'owner')
          .map((level) => (
            <LevelRow
              key={level.key}
              number={0}
              levelKey={level.key}
              standardRole={level.standard_role}
              memberCount={level.member_count}
              protectedLevel
              roles={[]}
              editable={false}
              onDropRole={() => undefined}
            />
          ))}
        {layout.map((level, index) => {
          const info = meta.get(level.key);
          const isStandard = level.key.startsWith('std:');
          const roleObjects = level.custom_role_ids
            .map((id) => byId.get(id))
            .filter((role): role is CustomRole => role !== undefined);
          return (
            <Fragment key={level.key}>
              {editable && index > 0 && (
                <div className={styles.insert}>
                  <button
                    type="button"
                    className={styles.insertButton}
                    disabled={gapIsFull(index - 1)}
                    onClick={() => {
                      insertAfter(index - 1);
                    }}
                  >
                    <Icons.plus /> {t('workspaceAdmin.hierarchy.addLevel')}
                  </button>
                </div>
              )}
              <LevelRow
                number={index + 1}
                levelKey={level.key}
                standardRole={isStandard ? (info?.standard_role ?? null) : null}
                memberCount={info?.member_count ?? 0}
                protectedLevel={false}
                roles={roleObjects}
                editable={editable}
                onDropRole={(roleId) => {
                  moveRole(roleId, level.key);
                }}
                onShiftRole={shiftRole}
                canShiftRoleUp={index > 0}
                canShiftRoleDown={index < layout.length - 1}
                onRemove={
                  !isStandard && level.custom_role_ids.length === 0
                    ? () => {
                        removeLevel(level.key);
                      }
                    : undefined
                }
                onShiftLevel={
                  isStandard
                    ? undefined
                    : (delta) => {
                        shiftLevel(index, delta);
                      }
                }
                canShiftLevelUp={index > 1}
                canShiftLevelDown={index < layout.length - 2}
              />
            </Fragment>
          );
        })}
      </div>
      <FormError error={save.error} />
      {editable && (
        <div className="hierarchy-actions" style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Button
            disabled={!dirty}
            onClick={() => {
              setDraft(null);
            }}
          >
            {t('workspaceAdmin.revertChanges')}
          </Button>
          <Button
            variant="primary"
            disabled={!dirty || save.isPending}
            onClick={() => {
              save.mutate(payload(true), {
                onSuccess: (result) => {
                  setPreview({
                    changes: result.changes,
                    added: result.added_levels,
                    removed: result.removed_levels,
                  });
                },
              });
            }}
          >
            {t('workspaceAdmin.hierarchy.review')}
          </Button>
        </div>
      )}
      {preview && (
        <Dialog
          title={t('workspaceAdmin.hierarchy.reviewTitle')}
          onClose={() => {
            setPreview(null);
          }}
        >
          <p>
            {t('workspaceAdmin.hierarchy.reviewSummary', {
              moved: preview.changes.length,
              added: preview.added,
              removed: preview.removed,
            })}
          </p>
          <ul className={styles.preview}>
            {preview.changes.map((change) => (
              <li key={change.role_id}>
                {t('workspaceAdmin.hierarchy.reviewRow', {
                  name: change.name,
                  people: change.member_count,
                })}
              </li>
            ))}
          </ul>
          <p>{t('workspaceAdmin.hierarchy.noPermissionChange')}</p>
          <FormError error={save.error} />
          <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => {
                setPreview(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={save.isPending}
              onClick={() => {
                save.mutate(payload(false, true), {
                  onSuccess: () => {
                    setPreview(null);
                    setDraft(null);
                    toast.show({ message: t('workspaceAdmin.saved') });
                  },
                });
              }}
            >
              {t('workspaceAdmin.hierarchy.apply')}
            </Button>
          </div>
        </Dialog>
      )}
    </section>
  );
}

interface LevelRowProps {
  number: number;
  levelKey: string;
  standardRole: MemberRole | null;
  memberCount: number;
  protectedLevel: boolean;
  roles: CustomRole[];
  editable: boolean;
  onDropRole: (roleId: string) => void;
  onShiftRole?: (roleId: string, delta: -1 | 1) => void;
  canShiftRoleUp?: boolean;
  canShiftRoleDown?: boolean;
  onRemove?: (() => void) | undefined;
  onShiftLevel?: ((delta: -1 | 1) => void) | undefined;
  canShiftLevelUp?: boolean;
  canShiftLevelDown?: boolean;
}

function LevelRow(props: LevelRowProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [over, setOver] = useState(false);
  const { onDropRole, editable } = props;

  useEffect(() => {
    const element = ref.current;
    if (!element || !editable) return;
    return dropTargetForElements({
      element,
      canDrop: ({ source }) => source.data.type === ROLE_DRAG,
      onDragEnter: () => {
        setOver(true);
      },
      onDragLeave: () => {
        setOver(false);
      },
      onDrop: ({ source }) => {
        setOver(false);
        if (typeof source.data.roleId === 'string') onDropRole(source.data.roleId);
      },
    });
  }, [editable, onDropRole]);

  const label = props.standardRole
    ? t(roleLabelKey(props.standardRole))
    : t('workspaceAdmin.hierarchy.customLevel');
  return (
    <div
      ref={ref}
      className={[
        styles.level,
        props.protectedLevel && styles.levelProtected,
        over && styles.levelOver,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="hierarchy-level"
      data-level={props.levelKey}
    >
      <div className={styles.levelHead}>
        <span className={styles.levelName}>
          {t('workspaceAdmin.hierarchy.level', { number: props.number })}
        </span>
        <span className={styles.levelMeta}>
          {props.protectedLevel
            ? t('workspaceAdmin.hierarchy.protected')
            : t('workspaceAdmin.roleMembers', { count: props.memberCount })}
        </span>
      </div>
      <div className={styles.chips}>
        {props.standardRole && (
          <span className={`${styles.chip} ${styles.chipStandard}`}>
            <span className={styles.chipName}>{label}</span>
            <span className={styles.badge}>
              {props.protectedLevel
                ? t('workspaceAdmin.roleProtected')
                : t('workspaceAdmin.roleStandard')}
            </span>
          </span>
        )}
        {props.roles.map((role) => (
          <RoleChip
            key={role.id}
            role={role}
            editable={editable}
            onShift={props.onShiftRole}
            canUp={props.canShiftRoleUp ?? false}
            canDown={props.canShiftRoleDown ?? false}
          />
        ))}
        {!props.standardRole && props.roles.length === 0 && (
          <span className={styles.levelMeta}>{t('workspaceAdmin.hierarchy.emptyLevel')}</span>
        )}
      </div>
      <div className={styles.levelActions}>
        {props.onShiftLevel && editable && (
          <>
            <button
              type="button"
              className={`${styles.mini} ${styles.up}`}
              disabled={!props.canShiftLevelUp}
              aria-label={t('workspaceAdmin.hierarchy.levelUp')}
              onClick={() => props.onShiftLevel?.(-1)}
            >
              <Icons.chevronDown />
            </button>
            <button
              type="button"
              className={styles.mini}
              disabled={!props.canShiftLevelDown}
              aria-label={t('workspaceAdmin.hierarchy.levelDown')}
              onClick={() => props.onShiftLevel?.(1)}
            >
              <Icons.chevronDown />
            </button>
          </>
        )}
        {props.onRemove && editable && (
          <button
            type="button"
            className={styles.mini}
            aria-label={t('workspaceAdmin.hierarchy.removeLevel')}
            onClick={props.onRemove}
          >
            <Icons.close />
          </button>
        )}
      </div>
    </div>
  );
}

function RoleChip({
  role,
  editable,
  onShift,
  canUp,
  canDown,
}: {
  role: CustomRole;
  editable: boolean;
  onShift: ((roleId: string, delta: -1 | 1) => void) | undefined;
  canUp: boolean;
  canDown: boolean;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLSpanElement>(null);
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element || !editable) return;
    return combine(
      draggable({
        element,
        getInitialData: () => ({ type: ROLE_DRAG, roleId: role.id }),
        onDragStart: () => {
          setDragging(true);
        },
        onDrop: () => {
          setDragging(false);
        },
      }),
    );
  }, [editable, role.id]);
  return (
    <span
      ref={ref}
      className={[styles.chip, editable && styles.chipCustom, dragging && styles.chipDragging]
        .filter(Boolean)
        .join(' ')}
      data-testid="hierarchy-role"
    >
      {role.color && <span className={styles.swatch} style={{ background: role.color }} />}
      <span className={styles.chipName}>{role.name}</span>
      <span className={styles.badge}>{t('workspaceAdmin.roleCustom')}</span>
      {editable && onShift && (
        <span className={styles.chipControls}>
          <button
            type="button"
            className={`${styles.mini} ${styles.up}`}
            disabled={!canUp}
            aria-label={t('workspaceAdmin.hierarchy.roleUp', { name: role.name })}
            onClick={() => {
              onShift(role.id, -1);
            }}
          >
            <Icons.chevronDown />
          </button>
          <button
            type="button"
            className={styles.mini}
            disabled={!canDown}
            aria-label={t('workspaceAdmin.hierarchy.roleDown', { name: role.name })}
            onClick={() => {
              onShift(role.id, 1);
            }}
          >
            <Icons.chevronDown />
          </button>
        </span>
      )}
    </span>
  );
}

/** Picks the level a role sits on (by rank) - the replacement of the old numeric seniority. */
export function LevelSelect({
  workspaceId,
  value,
  disabled,
  onChange,
}: {
  workspaceId: string;
  value: number;
  disabled?: boolean;
  onChange: (rank: number) => void;
}) {
  const { t } = useTranslation();
  const query = useRoleHierarchy(workspaceId);
  const options = (query.data?.levels ?? [])
    .map((level, index) => ({ level, index }))
    .filter(({ level }) => !level.protected)
    .map(({ level, index }) => ({
      value: String(level.rank),
      label: level.standard_role
        ? t('workspaceAdmin.hierarchy.levelWithRole', {
            number: index,
            role: t(roleLabelKey(level.standard_role)),
          })
        : t('workspaceAdmin.hierarchy.level', { number: index }),
    }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
        {t('workspaceAdmin.hierarchy.levelField')}
      </span>
      <Combobox
        label={t('workspaceAdmin.hierarchy.levelField')}
        value={String(value)}
        options={options}
        disabled={disabled ?? false}
        searchPlaceholder={t('common.search')}
        emptyText={t('common.noResults')}
        onChange={(next) => {
          onChange(Number(next));
        }}
      />
      <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>
        {t('workspaceAdmin.roleRankHint')}
      </span>
    </div>
  );
}
